import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { FadeUp } from "@/components/Reveal";

const COLLECTIONS = {
  "jojo-drop-001": {
    no: "COLLECTION 01 — JOJO DROP 001",
    title: "JOJO DROP 001",
    heroTitle: "NOT MADE TO BLEND IN.",
    line: "BOLD. EXPRESSIVE. UNRELENTING.",
    copy: "The jeans, the tees, the belts — the full JOJOS set, styled in purple, magenta, electric blue and gold.",
    heroImg: "/products/jeans-front.webp",
    testid: "collection-jojo-drop-001",
  },
  "evangelion-drop": {
    no: "COLLECTION 02",
    title: "EVANGELION DROP 002",
    heroTitle: "EVANGELION DROP 002.",
    line: "DARKER. HEAVIER. INDUSTRIAL.",
    copy: "Monochrome tones, industrial textures and restrained violet accents — a darker, futuristic edit.",
    heroImg: "/products/v3/hoodie-model-back.webp",
    testid: "collection-evangelion-drop",
  },
};

export default function Collection() {
  const { slug } = useParams();
  const meta = COLLECTIONS[slug];
  const [products, setProducts] = useState(null);

  useEffect(() => {
    setProducts(null);
    api
      .get("/products")
      .then((r) => setProducts(r.data.filter((p) => p.collection === slug)))
      .catch(() => setProducts([]));
  }, [slug]);

  useEffect(() => {
    if (meta) document.title = `${meta.title} — REQUIEM FEVER`;
  }, [meta]);

  if (!meta) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 bg-ink px-6 text-center">
        <p className="font-display text-5xl tracking-wide text-bone">COLLECTION NOT FOUND</p>
        <a href="/shop" data-testid="collection-notfound-back" className="border border-gold px-8 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-gold hover:bg-gold hover:text-ink">
          ALL COLLECTIONS
        </a>
      </div>
    );
  }

  return (
    <div className="bg-ink" data-testid={meta.testid}>
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={meta.heroImg} alt={meta.title} className="absolute inset-0 h-full w-full object-cover object-[center_72%] opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <FadeUp>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">{meta.no}</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h1 className="mt-4 font-display text-6xl leading-[0.9] tracking-[0.01em] text-bone sm:text-8xl">
              {meta.heroTitle}
            </h1>
          </FadeUp>
          <FadeUp delay={0.14}>
            <p className={`mt-5 font-mono text-[10px] uppercase tracking-[0.3em] ${meta.testid === "collection-evangelion-drop" ? "text-violet" : "text-gold"}`}>
              {meta.line}
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-bone/70">{meta.copy}</p>
          </FadeUp>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20">
        {products === null ? (
          <div className="py-20 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-bone/40">
            LOADING…
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-5 lg:grid-cols-4" data-testid={`${meta.testid}-grid`}>
            {products.map((p, i) => (
              <FadeUp key={p.slug} delay={(i % 4) * 0.07}>
                <ProductCard product={p} index={i} dark aspect="aspect-square" />
              </FadeUp>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
